// src/hooks/useProviderAssignmentRealtime.ts
//
// Subscribe to PROVIDER_ASSIGNMENT_* realtime events for a workspace
// and invalidate the relevant RTK Query tags so the list / detail /
// history / workload views refresh without a manual refetch.
//
// Safe to mount from multiple surfaces (Contact section, Circle tabs,
// Team member drawer) — realtimeClient refcounts the topic subscribe.

import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { realtimeClient } from '../realtime/socketClient';
import {
  RealtimeEventType,
  type RealtimeEventTypeValue,
  type ProviderAssignmentRealtimePayload,
} from '../realtime/events';
import { providerAssignmentsApi } from '../api/providerAssignmentsApi';

interface UseProviderAssignmentRealtimeOptions {
  workspaceId?: string | null;
  /** Optional: current context so we can invalidate its list slice too. */
  contactId?: string | null;
  providerUserId?: string | null;
  departmentCircleId?: string | null;
  onEvent?: (payload: ProviderAssignmentRealtimePayload, kind: string) => void;
}

const PA_EVENT_MAP: Array<[RealtimeEventTypeValue, string]> = [
  [RealtimeEventType.PROVIDER_ASSIGNMENT_CREATED, 'created'],
  [RealtimeEventType.PROVIDER_ASSIGNMENT_UPDATED, 'updated'],
  [RealtimeEventType.PROVIDER_ASSIGNMENT_REASSIGNED, 'reassigned'],
  [RealtimeEventType.PROVIDER_ASSIGNMENT_ENDED, 'ended'],
  [RealtimeEventType.PROVIDER_ASSIGNMENT_ROLE_CHANGED, 'role_changed'],
  [RealtimeEventType.PROVIDER_ASSIGNMENT_PRIORITY_CHANGED, 'priority_changed'],
  [RealtimeEventType.PROVIDER_ASSIGNMENT_STATUS_CHANGED, 'status_changed'],
];

export function useProviderAssignmentRealtime({
  workspaceId,
  contactId,
  providerUserId,
  departmentCircleId,
  onEvent,
}: UseProviderAssignmentRealtimeOptions) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();

  useEffect(() => {
    if (!workspaceId) return;
    const topic = `workspace:${workspaceId}`;
    realtimeClient.subscribe(topic);

    const invalidate = (
      payload: ProviderAssignmentRealtimePayload,
      kind: string,
    ) => {
      const tags: Array<{ type: 'ProviderAssignment'; id: string }> = [
        { type: 'ProviderAssignment', id: 'LIST' },
      ];

      // Ids on the payload
      const ids = new Set<string>();
      if (payload.assignmentId) ids.add(payload.assignmentId);
      if (payload.previousAssignmentId) ids.add(payload.previousAssignmentId);
      if (payload.newAssignmentId) ids.add(payload.newAssignmentId);
      for (const id of ids) {
        tags.push({ type: 'ProviderAssignment', id });
        tags.push({ type: 'ProviderAssignment', id: `${id}-history` });
      }
      if (payload.contactId) {
        tags.push({ type: 'ProviderAssignment', id: `contact-${payload.contactId}` });
      }
      if (payload.providerUserId) {
        tags.push({ type: 'ProviderAssignment', id: `provider-${payload.providerUserId}` });
        tags.push({ type: 'ProviderAssignment', id: `workload-${payload.providerUserId}` });
      }
      if (payload.departmentCircleId) {
        tags.push({ type: 'ProviderAssignment', id: `dept-${payload.departmentCircleId}` });
      }

      // Also invalidate the caller's known context (belt-and-suspenders
      // for payloads that omit the surrounding ids).
      if (contactId) tags.push({ type: 'ProviderAssignment', id: `contact-${contactId}` });
      if (providerUserId) {
        tags.push({ type: 'ProviderAssignment', id: `provider-${providerUserId}` });
        tags.push({ type: 'ProviderAssignment', id: `workload-${providerUserId}` });
      }
      if (departmentCircleId) {
        tags.push({ type: 'ProviderAssignment', id: `dept-${departmentCircleId}` });
      }

      dispatch(providerAssignmentsApi.util.invalidateTags(tags));
      onEvent?.(payload, kind);
    };

    const listeners = PA_EVENT_MAP.map(([eventType, kind]) =>
      realtimeClient.on<ProviderAssignmentRealtimePayload>(
        topic,
        (payload) => invalidate(payload, kind),
        eventType,
      ),
    );

    return () => {
      for (const off of listeners) off();
      realtimeClient.unsubscribe(topic);
    };
  }, [workspaceId, contactId, providerUserId, departmentCircleId, dispatch, onEvent]);
}

export default useProviderAssignmentRealtime;
